import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import DataTable, { ColDef } from '../util/DataTable';

// ── Типы превью по неделям (совпадают с backend RecalcWeeksPreview) ──
export interface WeekReportRow {
  id: number; date: string; totalHours: number;
  oldHPW: number; newHPW: number; oldSum: number; newSum: number;
}
export interface WeekAutoRow {
  id?: number; action: 'create' | 'update' | 'delete' | 'keep';
  date: string; oldSum: number; newSum: number;
}
export interface WeekManualRow {
  id: number; date: string; sum: number; description: string; willDelete: boolean;
}
export interface WeekPreview {
  weekKey: string; weekNum: number; year: number; start: string; end: string;
  patternName: string | null; hoursPerWeek: number; useToil: boolean;
  reports: WeekReportRow[]; autos: WeekAutoRow[]; manuals: WeekManualRow[];
  currentBalance: number; newBalance: number; changeCount: number; deleteManualCount: number;
}
export interface LeaveChange {
  id: number; startDate: string; endDate: string; category: string;
  oldHours: number; newHours: number; oldDays: number; newDays: number;
}
export interface RecalcWeeksPreview {
  weeks: WeekPreview[];
  leaveChanges: LeaveChange[];
  summary: { weeks: number; deleteManual: number; leave: number };
}

interface Props {
  userId: number;
  preview: RecalcWeeksPreview;      // caller уже получил превью (read-only)
  trigger?: 'manual' | 'on_change';
  onClose: () => void;
  onChanged?: () => void;           // применили что-то — обновить родителя (модалку не закрывать)
}

const fmt = (s: string) => { try { return format(new Date(`${s}T00:00:00`), 'dd MMM yyyy'); } catch { return s; } };
const n1 = (v: number) => Math.round((v ?? 0) * 100) / 100;
const bal = (v: number) => `${v > 0 ? '+' : ''}${n1(v)}h`;

const RecalcPreviewModal: React.FC<Props> = ({ userId, preview, trigger = 'manual', onClose, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [wp, setWp] = useState<RecalcWeeksPreview>(preview);
  const [applying, setApplying] = useState(false);
  const [confirm, setConfirm] = useState<{ kind: 'week' | 'all'; week?: WeekPreview } | null>(null);

  const warn = trigger === 'on_change';
  const totalDeleteManual = wp.summary?.deleteManual ?? 0;

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.post(`hr-recalc/preview-weeks/${userId}`);
      setWp(data);
    } catch { /* глобальный interceptor */ }
  }, [userId]);

  const doApply = async () => {
    if (!confirm) return;
    setApplying(true);
    try {
      if (confirm.kind === 'week' && confirm.week) {
        await axios.post(`hr-recalc/rebalance-week/${userId}`, { weekKey: confirm.week.weekKey });
      } else {
        await axios.post(`hr-recalc/rebalance-all/${userId}`);
      }
      enqueueSnackbar(t('Saved') as string, { variant: 'success' });
      setConfirm(null);
      await refresh();
      onChanged?.();
    } catch {
      enqueueSnackbar(t('Error') as string, { variant: 'error' });
    } finally { setApplying(false); }
  };

  // ── styles ──
  const seccount: React.CSSProperties = { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-color-calendar)', margin: '4px 2px 10px' };
  const dim: React.CSSProperties = { fontSize: 10.5, color: 'var(--text-color-calendar-week)', fontWeight: 500 };
  const oldC: React.CSSProperties = { color: '#ef4444', fontWeight: 800 };
  const newC: React.CSSProperties = { color: '#059669', fontWeight: 800 };
  const chip: React.CSSProperties = { display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 10.5, fontWeight: 800, background: 'rgba(37,99,235,.12)', color: '#2563eb' };
  const mini: React.CSSProperties = { fontSize: 9, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', margin: '12px 0 5px' };
  const ith: React.CSSProperties = { fontSize: 9.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', textAlign: 'left', padding: '5px 9px', borderBottom: '1px solid var(--border-input)', whiteSpace: 'nowrap' };
  const itd: React.CSSProperties = { fontSize: 12.5, padding: '7px 9px', borderBottom: '1px solid rgba(128,128,128,.12)', fontVariantNumeric: 'tabular-nums' };
  const tag = (bg: string, c: string): React.CSSProperties => ({ display: 'inline-block', padding: '1px 7px', borderRadius: 20, fontSize: 9.5, fontWeight: 800, background: bg, color: c });
  const tDel = tag('rgba(239,68,68,.13)', '#ef4444');
  const tKeep = tag('rgba(5,150,105,.13)', '#059669');
  const tAuto = tag('rgba(37,99,235,.12)', '#2563eb');
  const tRep = tag('rgba(124,58,237,.12)', '#7c3aed');
  const actionLabel: Record<string, string> = {
    create: t('HrCreate', 'create') as string,
    update: t('HrUpdate', 'update') as string,
    delete: t('Delete') as string,
    keep: t('HrKeep', 'keep') as string,
  };

  const badge = (useToil: boolean) => (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 800, background: useToil ? 'rgba(5,150,105,.13)' : 'rgba(180,83,9,.14)', color: useToil ? '#059669' : '#b45309' }}>
      {useToil ? (t('HrUsesToil', 'With TOIL') as string) : (t('HrNoToil', 'No TOIL') as string)}
    </span>
  );

  // ── weeks table columns ──
  const columns: ColDef<WeekPreview>[] = [
    {
      key: 'week', label: t('Week') as string, sortable: false, width: '150px',
      render: (wk) => (<div><b style={{ fontWeight: 800 }}>{`W${wk.weekNum} · ${wk.year}`}</b><div style={dim}>{fmt(wk.start)} – {fmt(wk.end)}</div></div>),
    },
    {
      key: 'pattern', label: t('HrPatternOfWeek', 'Pattern of week') as string, sortable: false,
      render: (wk) => (<div><span style={{ fontWeight: 600 }}>{wk.patternName || '—'}</span><div style={dim}>{n1(wk.hoursPerWeek)} {t('HrPerWeek', 'h/week')}</div></div>),
    },
    { key: 'toil', label: 'TOIL', sortable: false, width: '110px', render: (wk) => badge(wk.useToil) },
    {
      key: 'balance', label: t('HrWeekBalance', 'Week balance') as string, sortable: false, width: '165px',
      render: (wk) => (<span style={{ fontVariantNumeric: 'tabular-nums' }}><span style={oldC}>{bal(wk.currentBalance)}</span><span style={{ color: 'var(--text-color-calendar-week)', margin: '0 5px' }}>→</span><span style={newC}>{bal(wk.newBalance)}</span></span>),
    },
    { key: 'changes', label: t('HrChanges', 'Changes') as string, sortable: false, align: 'right', width: '90px', render: (wk) => <span style={chip}>{wk.changeCount}</span> },
  ];

  const renderExpanded = (wk: WeekPreview) => (
    <div style={{ padding: '4px 4px 14px' }}>
      {/* Объяснение: паттерн недели + use_toil */}
      <div style={{
        borderRadius: 9, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.5, marginBottom: 4, display: 'flex', gap: 9, alignItems: 'flex-start',
        background: wk.useToil ? 'rgba(5,150,105,.08)' : 'var(--bg-alert)',
        color: wk.useToil ? '#059669' : 'var(--text-alert)',
        border: `1px solid ${wk.useToil ? 'rgba(5,150,105,.25)' : 'rgba(180,83,9,.3)'}`,
      }}>
        <span>{wk.useToil ? '✓' : '⚠️'}</span>
        <div>
          {t('HrWeekPatternIs', 'Pattern in effect this week')}: <b>«{wk.patternName || '—'}»</b>.{' '}
          {wk.useToil
            ? t('HrWeekUsesToil', 'Pattern uses TOIL → overtime goes to the TOIL balance. Report is recalculated, any auto-correction is removed, manual corrections are kept.')
            : t('HrWeekNoToil', 'Pattern does NOT use TOIL → overtime is zeroed by an auto-correction, and all manual corrections for this week are removed (they duplicate the offset).')}
        </div>
      </div>

      {/* Report */}
      {wk.reports.length > 0 && (
        <>
          <div style={mini}>{t('HrReportRecalc', 'Report — recalculated by pattern')}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={ith}>{t('Date')}</th>
              <th style={{ ...ith, textAlign: 'right' }}>{t('Logged', 'Logged')}</th>
              <th style={{ ...ith, textAlign: 'right' }}>{t('HrPerWeek', 'h/week')}</th>
              <th style={{ ...ith, textAlign: 'right' }}>{t('HrToilWasNow', 'TOIL was → now')}</th>
            </tr></thead>
            <tbody>
              {wk.reports.map((r) => (
                <tr key={r.id}>
                  <td style={itd}>{fmt(r.date)} <span style={tRep}>report</span></td>
                  <td style={{ ...itd, textAlign: 'right' }}>{n1(r.totalHours)}h</td>
                  <td style={{ ...itd, textAlign: 'right' }}>{n1(r.oldHPW) !== n1(r.newHPW) ? <><span style={oldC}>{n1(r.oldHPW)}</span> → <span style={newC}>{n1(r.newHPW)}</span></> : n1(r.newHPW)}</td>
                  <td style={{ ...itd, textAlign: 'right' }}><span style={oldC}>{bal(r.oldSum)}</span> → <span style={newC}>{bal(r.newSum)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Auto */}
      {wk.autos.length > 0 && (
        <>
          <div style={mini}>{t('HrAutoCorrection', 'Auto-correction (zeroing overtime)')}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={ith}>{t('HrAction', 'Action')}</th>
              <th style={ith}>{t('Date')}</th>
              <th style={{ ...ith, textAlign: 'right' }}>{t('Was', 'Was')}</th>
              <th style={{ ...ith, textAlign: 'right' }}>{t('Now', 'Now')}</th>
            </tr></thead>
            <tbody>
              {wk.autos.map((a, i) => (
                <tr key={a.id ?? `c${i}`}>
                  <td style={itd}><span style={a.action === 'delete' ? tDel : a.action === 'keep' ? tKeep : tAuto}>{actionLabel[a.action]}</span></td>
                  <td style={itd}>{fmt(a.date)}</td>
                  <td style={{ ...itd, textAlign: 'right' }}>{a.action === 'create' ? '—' : <span style={oldC}>{bal(a.oldSum)}</span>}</td>
                  <td style={{ ...itd, textAlign: 'right' }}>{a.action === 'delete' ? '—' : <span style={newC}>{bal(a.newSum)}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Manual */}
      {wk.manuals.length > 0 && (
        <>
          <div style={mini}>{t('HrManualOfWeek', 'Manual corrections of the week')}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={ith}>{t('Date')}</th>
              <th style={{ ...ith, textAlign: 'right' }}>{t('HrSum', 'Sum')}</th>
              <th style={ith}>{t('Comment', 'Comment')}</th>
              <th style={{ ...ith, textAlign: 'right' }}>{t('HrWhatHappens', 'Result')}</th>
            </tr></thead>
            <tbody>
              {wk.manuals.map((m) => (
                <tr key={m.id} style={m.willDelete ? { textDecoration: 'line-through', opacity: .6 } : undefined}>
                  <td style={itd}>{fmt(m.date)}</td>
                  <td style={{ ...itd, textAlign: 'right', fontWeight: 700 }}>{bal(m.sum)}</td>
                  <td style={{ ...itd, textDecoration: 'none' }}>{m.description || '—'}</td>
                  <td style={{ ...itd, textAlign: 'right', textDecoration: 'none' }}><span style={m.willDelete ? tDel : tKeep}>{m.willDelete ? (t('Delete') as string) : (t('HrKeep', 'keep') as string)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Footer: net + rebalance week */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, gap: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>
          {t('HrWeekBalance', 'Week balance')}: <b style={{ ...oldC, fontSize: 13.5 }}>{bal(wk.currentBalance)}</b> → <b style={{ ...newC, fontSize: 13.5 }}>{bal(wk.newBalance)}</b>
        </div>
        <button type="button" className="btn-primary" style={{ fontSize: 12 }} onClick={() => setConfirm({ kind: 'week', week: wk })}>
          🔁 {t('HrRebalanceWeek', 'Rebalance week')}
        </button>
      </div>
    </div>
  );

  const hasWeeks = wp.weeks.length > 0;
  const hasLeave = wp.leaveChanges.length > 0;
  const nothing = !hasWeeks && !hasLeave;

  return (
    <div data-portal-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)' as any, background: 'rgba(10,15,35,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 900, maxWidth: '100%', maxHeight: '88vh', background: 'var(--bg-form)', borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 70px rgba(20,30,70,.35)' }}>
        {/* Header */}
        <div style={{ padding: '13px 18px', color: '#fff', display: 'flex', alignItems: 'center', gap: 10, background: warn ? 'linear-gradient(135deg,#b45309,#831843)' : 'linear-gradient(135deg,#1e3a8a,#4c1d95)' }}>
          <span style={{ fontSize: 17 }}>{warn ? '⚠️' : '🔁'}</span>
          <div style={{ flex: 1, fontSize: 14, fontWeight: 800 }}>{t('HrRebalanceTitle', 'Rebalance TOIL by week')}</div>
          <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,.15)', border: 'none', borderRadius: 7, color: '#fff', width: 26, height: 26, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Action bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 18px', borderBottom: 'var(--border-input)', background: 'var(--bg-top)' }}>
          <div style={{ display: 'flex', gap: 20 }}>
            <div><div style={{ ...dim, fontSize: 9, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('HrWeeksToFix', 'Weeks to fix')}</div><div style={{ fontSize: 18, fontWeight: 900 }}>{wp.summary?.weeks ?? 0}</div></div>
            <div><div style={{ ...dim, fontSize: 9, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('HrDeleteManual', 'Delete manual')}</div><div style={{ fontSize: 18, fontWeight: 900, color: totalDeleteManual ? '#ef4444' : 'var(--text-color)' }}>{totalDeleteManual}</div></div>
            {hasLeave && <div><div style={{ ...dim, fontSize: 9, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('HrLeave', 'Annual leave')}</div><div style={{ fontSize: 18, fontWeight: 900 }}>{wp.summary?.leave ?? 0}</div></div>}
          </div>
          {(hasWeeks || hasLeave) && (
            <button type="button" className="btn-primary" style={{ fontSize: 12.5, fontWeight: 800 }} disabled={applying} onClick={() => setConfirm({ kind: 'all' })}>
              🔁 {t('HrRebalanceAll', 'Rebalance all')} ({(wp.summary?.weeks ?? 0) + (wp.summary?.leave ?? 0)})
            </button>
          )}
        </div>

        {/* Body */}
        <div className="thin-scroll" style={{ flex: 1, overflow: 'auto', padding: '14px 18px' }}>
          {warn && !nothing && (
            <div style={{ background: 'rgba(245,158,11,.12)', color: '#b45309', border: '1px solid rgba(245,158,11,.3)', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, fontWeight: 600, marginBottom: 14 }}>
              {t('HrRecalcWarnBody', 'Pattern saved. Discrepancies were found in TOIL and leave. Review by week and rebalance, or close to keep current values.')}
            </div>
          )}

          {nothing ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-color-calendar-week)', fontSize: 13 }}>
              ✓ {t('HrRecalcNoChanges', 'No changes — everything is up to date.')}
            </div>
          ) : (
            <>
              {hasWeeks && (
                <>
                  <div style={seccount}>{t('HrToilByWeek', 'TOIL by week')} · {wp.summary?.weeks ?? 0}</div>
                  <DataTable<WeekPreview>
                    rows={wp.weeks}
                    columns={columns}
                    getKey={(wk) => wk.weekKey}
                    renderExpanded={renderExpanded}
                    filterable={false}
                    columnsToggle={false}
                    exportable={false}
                    selectable={false}
                    hideToolbar
                    noCard
                    pagination={false}
                  />
                </>
              )}

              {hasLeave && (
                <>
                  <div style={{ ...seccount, marginTop: 20 }}>{t('HrLeave', 'Annual leave')} · {wp.summary?.leave ?? 0}</div>
                  <div style={{ overflowX: 'auto', border: 'var(--border-input)', borderRadius: 10 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr>
                        <th style={{ ...ith, padding: '9px 14px' }}>{t('Period')}</th>
                        <th style={{ ...ith, textAlign: 'right' }}>{t('Was', 'Was')}</th>
                        <th style={{ ...ith, textAlign: 'right', padding: '9px 14px' }}>{t('Now', 'Now')}</th>
                      </tr></thead>
                      <tbody>
                        {wp.leaveChanges.map((c) => (
                          <tr key={c.id}>
                            <td style={{ ...itd, padding: '8px 14px' }}>{fmt(c.startDate)} → {fmt(c.endDate)}</td>
                            <td style={{ ...itd, textAlign: 'right' }}><span style={oldC}>{n1(c.oldHours)}h / {n1(c.oldDays)}d</span></td>
                            <td style={{ ...itd, textAlign: 'right', padding: '8px 14px' }}><span style={newC}>{n1(c.newHours)}h / {n1(c.newDays)}d</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 18px', borderTop: 'var(--border-input)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: 'var(--bg-top-fix)' }}>
          <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{t('HrRebalanceNote', 'Each apply writes an audit-log entry. Nothing changes without confirmation.')}</span>
          <button type="button" className="dt-btn-ghost" style={{ fontSize: 12 }} onClick={onClose}>{t('Close')}</button>
        </div>
      </div>

      {/* Confirm dialog */}
      {confirm && (
        <div onClick={() => !applying && setConfirm(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-toast)' as any, background: 'rgba(10,15,35,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: 460, maxWidth: '100%', background: 'var(--bg-form)', borderRadius: 13, overflow: 'hidden', boxShadow: '0 24px 70px rgba(20,30,70,.4)' }}>
            <div style={{ padding: '13px 16px', background: 'linear-gradient(135deg,#b45309,#831843)', color: '#fff', fontSize: 13.5, fontWeight: 800, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>⚠️</span>
              {confirm.kind === 'week' && confirm.week
                ? `${t('HrRebalanceWeek', 'Rebalance week')} W${confirm.week.weekNum} · ${confirm.week.year}?`
                : `${t('HrRebalanceAll', 'Rebalance all')}?`}
            </div>
            <div style={{ padding: '15px 16px', fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-color)' }}>
              {confirm.kind === 'week' && confirm.week ? (
                <>
                  {t('HrWeekPatternIs', 'Pattern in effect this week')}: <b>«{confirm.week.patternName || '—'}»</b> — {confirm.week.useToil ? badge(true) : badge(false)}.
                  <ul style={{ listStyle: 'none', margin: '10px 0 4px', padding: 0 }}>
                    {confirm.week.reports.some((r) => Math.abs(r.oldSum - r.newSum) > 0.01 || Math.abs(r.oldHPW - r.newHPW) > 0.01) && (
                      <li style={{ padding: '5px 0', display: 'flex', gap: 8, borderBottom: '1px solid rgba(128,128,128,.12)' }}><span style={tRep}>report</span><span>{t('HrRecalcReportLine', 'Recalculate report by pattern')}</span></li>
                    )}
                    {confirm.week.autos.filter((a) => a.action !== 'keep').map((a, i) => (
                      <li key={`a${i}`} style={{ padding: '5px 0', display: 'flex', gap: 8, borderBottom: '1px solid rgba(128,128,128,.12)' }}>
                        <span style={a.action === 'delete' ? tDel : tAuto}>{actionLabel[a.action]}</span>
                        <span>{t('HrAutoCorrection', 'Auto-correction')}: {a.action === 'delete' ? <b style={oldC}>{bal(a.oldSum)}</b> : <b style={newC}>{bal(a.newSum)}</b>}</span>
                      </li>
                    ))}
                    {confirm.week.manuals.filter((m) => m.willDelete).map((m) => (
                      <li key={`m${m.id}`} style={{ padding: '5px 0', display: 'flex', gap: 8, borderBottom: '1px solid rgba(128,128,128,.12)' }}>
                        <span style={tDel}>{t('Delete') as string}</span>
                        <span>{t('HrManualCorr', 'Manual correction')} {fmt(m.date)} (<b style={oldC}>{bal(m.sum)}</b>{m.description ? `, «${m.description}»` : ''})</span>
                      </li>
                    ))}
                  </ul>
                  <div style={{ marginTop: 10, fontWeight: 800 }}>{t('HrWeekResult', 'Week result')}: <span style={oldC}>{bal(confirm.week.currentBalance)}</span> → <span style={newC}>{bal(confirm.week.newBalance)}</span></div>
                </>
              ) : (
                <>
                  {t('HrRebalanceAllBody', 'All weeks below will be rebalanced at once, and annual leave will be recalculated.')}
                  <ul style={{ listStyle: 'none', margin: '10px 0 4px', padding: 0 }}>
                    <li style={{ padding: '5px 0', display: 'flex', gap: 8, borderBottom: '1px solid rgba(128,128,128,.12)' }}><span style={tAuto}>{wp.summary?.weeks ?? 0}</span><span>{t('HrWeeksToRebalance', 'weeks to rebalance')}</span></li>
                    <li style={{ padding: '5px 0', display: 'flex', gap: 8, borderBottom: '1px solid rgba(128,128,128,.12)' }}><span style={tDel}>{totalDeleteManual}</span><span>{t('HrManualToDelete', 'manual corrections to delete')}</span></li>
                    {hasLeave && <li style={{ padding: '5px 0', display: 'flex', gap: 8 }}><span style={tKeep}>{wp.summary?.leave ?? 0}</span><span>{t('HrLeaveToRecalc', 'leave periods to recalculate')}</span></li>}
                  </ul>
                </>
              )}
            </div>
            <div style={{ padding: '11px 16px', borderTop: 'var(--border-input)', display: 'flex', justifyContent: 'flex-end', gap: 8, background: 'var(--bg-top-fix)' }}>
              <button type="button" className="dt-btn-ghost" style={{ fontSize: 12 }} disabled={applying} onClick={() => setConfirm(null)}>{t('Cancel')}</button>
              <button type="button" style={{ fontSize: 12, fontWeight: 800, background: 'linear-gradient(135deg,#b45309,#831843)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', opacity: applying ? .6 : 1 }} disabled={applying} onClick={doApply}>
                {t('HrRebalance', 'Rebalance')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecalcPreviewModal;
